import { authFetch } from 'auth/utils';

import { SimInventorySearchParamsV2, SimInventorySort } from 'simInventory/models/SimProfile.model';
import { preparePaginatedSimInventorySearchBody } from 'simInventory/utils/prepareSimInventorySearchBody';

import { ErrorCodes } from 'src/common/responseCodes';
import { AssignedTagToSimDTO } from '@/tags/models/tagsApi.types';
import { normilizeAssignedTagsToSim } from 'tags/utils/mappers';
import { fetchAssignedTagsToSimProducts } from '../Tags/services/tagsSimProductApi';
import { envVariables } from 'src/common/utils/envVariables';
import { fetchSimProductsTableConfiguration } from '../Inventory/api/simProductsTableConfigurationApi';
import { mapSimDTO } from 'src/common/components/Sim/Inventory/utils/simListResponseParsers';
import { SimDTO } from 'src/common/components/Sim/constants/sim';
import { SimListResponsePagination } from 'src/common/components/Sim/Inventory/types/simListTypes';
import { ErrorResponse } from 'src/common/types/apiTypes';

export const fetchSimProductsWithConfiguration = async (
  page: number,
  entryPerPage: number,
  searchParams: SimInventorySearchParamsV2,
  sort: SimInventorySort | null = null,
  signal?: AbortSignal,
) => {
  const body = preparePaginatedSimInventorySearchBody(page, entryPerPage, searchParams, sort);

  const simProductsResponse = await authFetch(`${envVariables.VITE_SIM_PRODUCTS_API}`, {
    signal,
    method: 'POST',
    body: JSON.stringify(body),
    headers: {
      'Content-Type': 'application/json',
    },
  });

  const simProducts: SimDTO[] = await simProductsResponse.json();

  const tags: AssignedTagToSimDTO[] = await fetchAssignedTagsToSimProducts({
    iccids: simProducts.map((sim) => sim.iccid),
  });
  const normalizedTagsData = normilizeAssignedTagsToSim(tags);

  const configuration = await fetchSimProductsTableConfiguration();

  return {
    items: simProducts.map((item) => mapSimDTO(item, { normalizedTagsData })),
    configuration,
  };
};

export const fetchSimProductsPagination = async (
  page: number,
  entryPerPage: number,
  searchParams: SimInventorySearchParamsV2,
  signal?: AbortSignal,
) => {
  const body = preparePaginatedSimInventorySearchBody(page, entryPerPage, searchParams);

  const simProductsPaginationResponse = await authFetch(
    `${envVariables.VITE_SIM_PRODUCTS_API}/pagination`,
    {
      signal,
      method: 'POST',
      body: JSON.stringify(body),
      headers: {
        'Content-Type': 'application/json',
      },
    },
  );

  const simInventoryPaginationResponseData: SimListResponsePagination =
    await simProductsPaginationResponse.json();

  return {
    totalNumberOfItems: simInventoryPaginationResponseData.totalCount ?? -1,
  };
};

export const fetchSimProductEntry = async (iccid: string, signal?: AbortSignal) => {
  const simInventoryEntryResponse = await authFetch(
    `${envVariables.VITE_SIM_PRODUCTS_API}/${iccid}`,
    { signal },
  );

  if (!simInventoryEntryResponse.ok) {
    const status = simInventoryEntryResponse.status.toString();

    if (status === ErrorCodes.NotFound) {
      throw new Error(ErrorCodes.NotFound);
    }

    let errorMessage: ErrorResponse;
    try {
      errorMessage = await simInventoryEntryResponse.json();
    } catch {
      throw new Error(status);
    }

    const iccidNotFoundErrors = errorMessage.validationErrors.Iccid?.find(
      (error) => error.code === 'IccidValidator',
    );

    if (status === ErrorCodes.BadRequest && iccidNotFoundErrors) {
      throw new Error(ErrorCodes.NotFound);
    }

    throw new Error(status);
  }

  const simInventoryItem: SimDTO = await simInventoryEntryResponse.json();

  const tags = await fetchAssignedTagsToSimProducts({ iccids: [iccid] });
  const normalizedTagsData = normilizeAssignedTagsToSim(tags);
  const simDetail = mapSimDTO(simInventoryItem, { normalizedTagsData });

  return simDetail;
};
